import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./TeacherSection.css";
import Footer from "../pages/Footer";
import { SquareCheck } from 'lucide-react';
import { FaUserPlus } from 'react-icons/fa';
import { MdLock, MdDashboard } from 'react-icons/md';


const TeacherSection = () => {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);
  const navigate = useNavigate();


  return (
    <>
    <section className="tsm-section-1_0_0_" id="teacher" aria-label="For Teachers">
      <div className="tsm-container-1_0_1_">
        {/* Intro */}
        <div className="tsm-intro-1_0_2_" data-aos="fade-up">
          <h2>
            For <span className="tsm-accent-1_0_3_">Teachers</span> & Professors
          </h2>
          <p className="tsm-sub-1_0_4_">
            BodhAI provides a secure & transparent teacher onboarding: register, submit
            verification docs, get admin approval, then access a powerful teacher dashboard.
          </p>

          <div className="tsm-ctaRow-1_0_5_">
            <button onClick={() => navigate("/teacher-registration")}><FaUserPlus /> Register</button>
            <button className="tsm-outline-1_0_6_" onClick={() => navigate("/teacher-login")}><MdLock /> Login</button>
            <button className="tsm-ghost-1_0_7_" onClick={() => navigate("/teacher-dashboard")}><MdDashboard /> Dashboard</button>
          </div>
        </div>

        {/* Hero */}
        <div className="tsm-hero-1_1_0_" data-aos="fade-up">
          <div className="tsm-heroText-1_1_1_" data-aos="fade-right">
            <ul className="tsm-bullets-1_1_2_">
              <li>Create topic-wise tests (MCQs & coding) in minutes.</li>
              <li>Assign  tests to classes, set deadlines, auto-evaluate.</li>
              <li>Deep analytics: accuracy, speed, weak-topic insights.</li>
              <li>Role-based access & admin-verified teacher accounts.</li>
            </ul>

            <div className="tsm-stats-1_1_3_">
              <div className="tsm-stat-1_1_4_">
                <span>6</span>
                <small>Simple Steps</small>
              </div>
              <div className="tsm-stat-1_1_4_">
                <span>100%</span>
                <small>Admin Verified</small>
              </div>
              <div className="tsm-stat-1_1_4_">
                <span>RBAC</span>
                <small>Secure Access</small>
              </div>
            </div>
          </div>

          <div className="tsm-heroArt-1_1_5_" data-aos="fade-left">
            <img src="/images/teacher.png" alt="Teacher guiding students" loading="lazy" />
          </div>
        </div>

        {/* Timeline / Steps */}
        <div className="tsm-timeline-1_2_0_" data-aos="fade-up">
          <h3>Onboarding Flow</h3>
          <ol className="tsm-steps-1_2_1_">
            <li>
              <h4>Register</h4>
              <p>Enter <b>Name</b>, <b>Email</b>, <b>Phone</b>, <b>School/College</b>, <b>Designation</b>, and a short bio.</p>
            </li>
            <li>
              <h4>Upload Documents</h4>
              <p>Paste Google Drive links for your <b>ID Card</b> & <b>Aadhaar</b>. Set them to “view only”.</p>
            </li>
            <li>
              <h4>Pending Review</h4>
              <p>Your application status is <b>Pending</b> while Admin verifies details.</p>
            </li>
            <li>
              <h4>Approval & Credentials</h4>
              <p>On approval, you’ll receive a notification and a system-generated password via email.</p>
            </li>
            <li>
              <h4>Login</h4>
              <p>Use your <b>Email + Password</b> to sign in. If still pending, you’ll see an approval notice.</p>
            </li>
            <li>
              <h4>Dashboard Access</h4>
              <p>Create tests, upload MCQs/coding, manage classes & view analytics.</p>
            </li>
          </ol>

          <div className="tsm-statusBox-1_2_2_">
            <p>
              Already applied?{" "}
              <button className="tsm-linkBtn-1_2_3_" onClick={() => navigate("/teacher-login")}>
                Check your application status →
              </button>
            </p>
          </div>
        </div>

        {/* Requirements */}
        <div className="tsm-req-1_3_0_" data-aos="fade-up">
          <h3>What you’ll need for registration</h3>
          <div className="tsm-reqGrid-1_3_1_">
            <div className="tsm-reqCard-1_3_2_"><SquareCheck color="green"/> Full Name, Email & Phone</div>
            <div className="tsm-reqCard-1_3_2_"><SquareCheck color="green"/> School/College & Designation</div>
            <div className="tsm-reqCard-1_3_2_"><SquareCheck color="green"/> Short bio / About</div>
            <div className="tsm-reqCard-1_3_2_"><SquareCheck color="green"/> Google Drive link: ID Card</div>
            <div className="tsm-reqCard-1_3_2_"><SquareCheck color="green"/> Google Drive link: Aadhaar Card</div>
            <div className="tsm-reqCard-1_3_2_"><SquareCheck color="green"/> Links set to “Anyone can view”</div>
          </div>
          <p className="tsm-note-1_3_3_">
            Privacy tip: Aadhaar number ko plain text me na likhen—document me hi rahe. Drive links “view only” rakhiyega.
          </p>
        </div>

        {/* Features */}
        <div className="tsm-features-1_4_0_" data-aos="fade-up">
          <h3>Why teach with BodhAI</h3>
          <div className="tsm-featureGrid-1_4_1_">
            <div className="tsm-featureCard-1_4_2_">
              <h4>AI-Generated Tests</h4>
              <p>Instant MCQs & coding challenges with solutions & test cases.</p>
            </div>
            <div className="tsm-featureCard-1_4_2_">
              <h4>Class & Groups</h4>
              <p>Student groups, Tests, deadlines & reminders.</p>
            </div>
            <div className="tsm-featureCard-1_4_2_">
              <h4>Powerful Analytics</h4>
              <p>Topic-wise accuracy, time per question, improvement charts.</p>
            </div>
            <div className="tsm-featureCard-1_4_2_">
              <h4>Secure & Verified</h4>
              <p>Admin-approved accounts with role-based permissions.</p>
            </div>
            <div className="tsm-featureCard-1_4_2_">
              <h4>Export & Share</h4>
              <p>Download results as CSV/PDF and share with peers.</p>
            </div>
            <div className="tsm-featureCard-1_4_2_">
              <h4>Priority Support</h4>
              <p>Verified teachers get fast support and onboarding help.</p>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="tsm-faq-1_5_0_" data-aos="fade-up">
          <h3>FAQs</h3>

          <details>
            <summary>How long does approval take?</summary>
            <p>Generally, your request is reviewed within 24–72 hours. You’ll receive an email notification once it’s done.</p>
          </details>

          <details>
            <summary>What if my documents are private in Google Drive?</summary>
            <p>Make sure the sharing setting is set to “Anyone with the link → View” or add the admin email as a viewer.</p>
          </details>

          <details>
            <summary>I forgot my password. How can I reset it?</summary>
            <p>Use the “Forgot Password” option on the login page. Enter your registered email and follow the reset instructions.</p>
          </details>

          <details>
            <summary>Why does it show ‘Pending’ on my login?</summary>
            <p>This means your account is still waiting for admin approval. You can track progress on the Status page.</p>
          </details>

          <details>
            <summary>Can I update my profile after registration?</summary>
            <p>Yes, once your account is approved, you can edit your profile details from the Dashboard → Profile section.</p>
          </details>

          <details>
            <summary>How do teachers access the dashboard?</summary>
            <p>Teachers can log in with their registered email and password. The dashboard will show teaching tools, student lists, and resources.</p>
          </details>

          <details>
            <summary>Is my data secure?</summary>
            <p>Yes. We use Role-Based Access Control (RBAC), secure encryption, and strict privacy policies to protect your data.</p>
          </details>

          <details>
            <summary>Can students directly message teachers?</summary>
            <p>Yes, students can send queries via the internal messaging system. Teachers will be notified on their dashboard.</p>
          </details>

          <details>
            <summary>What happens if my application is rejected?</summary>
            <p>You’ll receive an email with the reason. You can re-apply after fixing the issues mentioned by the admin.</p>
          </details>

        </div>

        {/* Final CTA */}
        <div className="tsm-ctaBanner-1_6_0_" data-aos="zoom-in">
          <h3>Ready to get started?</h3>
          <div className="tsm-ctaRow-1_6_1_">
            <button onClick={() => navigate("/teacher-registration")}><FaUserPlus />  Register as Teacher</button>
            <button className="tsm-outline-1_6_2_" onClick={() => navigate("/teacher-login")}><MdLock /> Login</button>
            <button className="tsm-ghost-1_6_3_" onClick={() => navigate("/teacher-dashboard")}><MdDashboard /> Go to Dashboard</button>
          </div>
        </div>
      </div>

       

    </section>
    <Footer />

    </>

  );
};

export default TeacherSection;
